"use client";

import { useLocaleCurrency } from "@shared/hooks/locale-currency";
import { Button } from "@ui/components/button";
import { Tabs, TabsList, TabsTrigger } from "@ui/components/tabs";
import { cn } from "@ui/lib";
import type { ApiOutput } from "api/trpc/router";
import { Check, } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type SubscriptionPlan = ApiOutput["billing"]["plans"][number] & {
  features?: string[];
};

export function PricingTable({
  plans,
  activePlanId,
  onSelectPlan,
  labels,
  className,
}: {
  plans: SubscriptionPlan[];
  activePlanId?: string;
  onSelectPlan: (planId: string, variantId: string) => void | Promise<void>;
  className?: string;
  labels: {
    yearly: string;
    monthly: string;
    month: string;
    year: string;
    subscribe: string;
    currentPlan?: string;
    switchToPlan?: string;
  };
}) {
  const localeCurrency = useLocaleCurrency();
  const [interval, setInterval] = useState<string>("");
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const availableIntervals = useMemo(() => {
    return Array.from(
      new Set(plans.flatMap((plan) => plan.variants.map((v) => v.interval)))
    );
  }, [plans]);
  const sortedAndFilteredPlans = useMemo(() => {
    return [...plans]
      .map((plan) => {
        const variants = plan.variants
          .filter((v) => v.interval === interval)
          .sort((a, b) => a.price - b.price);

        return {
          ...plan,
          variants,
        };
      })
      .filter((plan) => plan.variants.length > 0)
      .sort((a, b) => {
        const lowestPriceA = a.variants.reduce(
          (lowest, variant) => Math.min(lowest, variant.price),
          Number.POSITIVE_INFINITY
        );
        const lowestPriceB = b.variants.reduce(
          (lowest, variant) => Math.min(lowest, variant.price),
          Number.POSITIVE_INFINITY
        );

        return lowestPriceA - lowestPriceB;
      });
  }, [plans, interval, localeCurrency]);

  useEffect(() => {
    const defaultInterval = plans[0]?.variants[0]?.interval;

    // Defina um default apenas na inicialização.
    if (!interval) {
      if (defaultInterval) {
        setInterval(defaultInterval);
      }
      return;
    }

    // Se o intervalo atual não existir mais (mudança de planos), volte para o default.
    const intervalExists = plans.some((plan) =>
      plan.variants.some((v) => v.interval === interval)
    );
    if (!intervalExists && defaultInterval) {
      setInterval(defaultInterval);
    }
  }, [plans, interval]);

  const isActivePlan = (plan: (typeof plans)[number]) => {
    return activePlanId === plan.id;
  };

  return (
    <div className={cn(className, "@container")}>
      <div className="flex justify-center">
        {availableIntervals.includes("month") &&
          availableIntervals.includes("year") && (
            <Tabs
              value={interval}
              onValueChange={(value) => setInterval(value as typeof interval)}
              className="mb-4"
              data-test="price-table-interval-tabs"
            >
              <TabsList>
                <TabsTrigger value="month">{labels.monthly}</TabsTrigger>
                <TabsTrigger value="year">{labels.yearly}</TabsTrigger>
              </TabsList>
            </Tabs>
          )}
      </div>
      <div className="flex justify-center">
        {sortedAndFilteredPlans.map((plan) => {
          const variant = plan.variants.find((v) => v.interval === interval);

          if (!variant) {
            return null;
          }

          return (
            <div key={plan.id} className="w-full px-4 max-w-md">
              <div className="relative z-10 mb-10 overflow-hidden rounded-[10px] border-2 border-[#111] bg-card/50 px-8 py-10 shadow-pricing sm:p-12 lg:px-6 lg:py-10 xl:p-[50px] dark:border-dark-3 dark:bg-dark-2">
                <span className="block text-lg font-semibold text-primary">
                  {plan.name}
                </span>
                <h2 className="mb-5 text-[42px] font-bold text-dark dark:text-white">
                  <span>
                    {Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: variant.currency,
                    }).format(variant.price / 100)}
                  </span>
                  <span className="text-base font-medium text-body-color dark:text-dark-6">
                    {" / "}
                    {labels[interval as keyof typeof labels]}
                  </span>
                </h2>
                {plan.description && (
                  <p
                    className="mb-8 border-b border-stroke pb-8 text-base text-body-color dark:border-dark-3 dark:text-dark-6"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
                    dangerouslySetInnerHTML={{ __html: plan.description }}
                  />
                )}
                <div className="mb-9 flex flex-col gap-[14px]">
                  <p className="text-base text-body-color dark:text-dark-6">
                    <Check className="inline text-primary" /> Estoque Ilimitado
                  </p>
                  <p className="text-base text-body-color dark:text-dark-6">
                    <Check className="inline text-primary" /> Página exclusiva
                    da sua loja
                  </p>
                </div>

                <Button
                  variant="secondary"
                  size="lg"
                  disabled={isActivePlan(plan)}
                  loading={selectedPlan === plan.id}
                  className="mt-6 w-full"
                  onClick={async () => {
                    setSelectedPlan(plan.id);
                    await onSelectPlan(plan.id, String(variant.id));
                    setSelectedPlan(null);
                  }}
                >
                  {isActivePlan(plan)
                    ? labels.currentPlan
                    : activePlanId
                      ? labels.switchToPlan
                      : labels.subscribe}
                </Button>

                {/* Divisor "ou" */}
                <div className="my-4 flex items-center gap-4">
                  <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    ou
                  </span>
                  <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
                </div>

                {/* Link para WhatsApp */}
                <a
                  href="https://wa.me/551131631333?text=Olá! Gostaria de saber mais sobre os planos do Click Autos."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg underline text-center block font-medium text-green-600 hover:text-green-700 transition-colors duration-200"
                >
                  <span>
                    <svg
                      className="w-6 h-6 mr-2 inline-block transition-transform duration-100 group-hover:scale-110 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <title>WhatsApp</title>
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.051 3.488" />
                    </svg>
                    Falar com um especialista
                  </span>
                </a>
                {/* <a
                    href="javascript:void(0)"
                    className="block w-full rounded-md border border-stroke bg-transparent p-3 text-center text-base font-medium text-primary transition hover:border-primary hover:bg-primary hover:text-white dark:border-dark-3"
                  >
                    Assinar {plan.name}
                  </a> */}

                <div>
                  <span className="absolute right-0 top-7 z-[-1]">
                    <svg
                      width="77"
                      height="172"
                      viewBox="0 0 77 172"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <circle
                        cx="86"
                        cy="86"
                        r="86"
                        fill="url(#paint0_linear)"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear"
                          x1="86"
                          y1="0"
                          x2="86"
                          y2="172"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#3056D3" stopOpacity="0.09" />
                          <stop
                            offset="1"
                            stopColor="#C4C4C4"
                            stopOpacity="0"
                          />
                        </linearGradient>
                      </defs>
                    </svg>
                  </span>
                  <span className="absolute right-4 top-4 z-[-1]">
                    <svg
                      width="41"
                      height="89"
                      viewBox="0 0 41 89"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <circle
                        cx="38.9138"
                        cy="87.4849"
                        r="1.42021"
                        transform="rotate(180 38.9138 87.4849)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="38.9138"
                        cy="74.9871"
                        r="1.42021"
                        transform="rotate(180 38.9138 74.9871)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="38.9138"
                        cy="62.4892"
                        r="1.42021"
                        transform="rotate(180 38.9138 62.4892)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="38.9138"
                        cy="38.3457"
                        r="1.42021"
                        transform="rotate(180 38.9138 38.3457)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="38.9138"
                        cy="13.634"
                        r="1.42021"
                        transform="rotate(180 38.9138 13.634)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="38.9138"
                        cy="50.2754"
                        r="1.42021"
                        transform="rotate(180 38.9138 50.2754)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="38.9138"
                        cy="26.1319"
                        r="1.42021"
                        transform="rotate(180 38.9138 26.1319)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="38.9138"
                        cy="1.42021"
                        r="1.42021"
                        transform="rotate(180 38.9138 1.42021)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="26.4157"
                        cy="87.4849"
                        r="1.42021"
                        transform="rotate(180 26.4157 87.4849)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="26.4157"
                        cy="74.9871"
                        r="1.42021"
                        transform="rotate(180 26.4157 74.9871)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="26.4157"
                        cy="62.4892"
                        r="1.42021"
                        transform="rotate(180 26.4157 62.4892)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="26.4157"
                        cy="38.3457"
                        r="1.42021"
                        transform="rotate(180 26.4157 38.3457)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="26.4157"
                        cy="13.634"
                        r="1.42021"
                        transform="rotate(180 26.4157 13.634)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="26.4157"
                        cy="50.2754"
                        r="1.42021"
                        transform="rotate(180 26.4157 50.2754)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="26.4157"
                        cy="26.1319"
                        r="1.42021"
                        transform="rotate(180 26.4157 26.1319)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="26.4157"
                        cy="1.4202"
                        r="1.42021"
                        transform="rotate(180 26.4157 1.4202)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="13.9177"
                        cy="87.4849"
                        r="1.42021"
                        transform="rotate(180 13.9177 87.4849)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="13.9177"
                        cy="74.9871"
                        r="1.42021"
                        transform="rotate(180 13.9177 74.9871)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="13.9177"
                        cy="62.4892"
                        r="1.42021"
                        transform="rotate(180 13.9177 62.4892)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="13.9177"
                        cy="38.3457"
                        r="1.42021"
                        transform="rotate(180 13.9177 38.3457)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="13.9177"
                        cy="13.634"
                        r="1.42021"
                        transform="rotate(180 13.9177 13.634)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="13.9177"
                        cy="50.2754"
                        r="1.42021"
                        transform="rotate(180 13.9177 50.2754)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="13.9177"
                        cy="26.1319"
                        r="1.42021"
                        transform="rotate(180 13.9177 26.1319)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="13.9177"
                        cy="1.42019"
                        r="1.42021"
                        transform="rotate(180 13.9177 1.42019)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="1.41963"
                        cy="87.4849"
                        r="1.42021"
                        transform="rotate(180 1.41963 87.4849)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="1.41963"
                        cy="74.9871"
                        r="1.42021"
                        transform="rotate(180 1.41963 74.9871)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="1.41963"
                        cy="62.4892"
                        r="1.42021"
                        transform="rotate(180 1.41963 62.4892)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="1.41963"
                        cy="38.3457"
                        r="1.42021"
                        transform="rotate(180 1.41963 38.3457)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="1.41963"
                        cy="13.634"
                        r="1.42021"
                        transform="rotate(180 1.41963 13.634)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="1.41963"
                        cy="50.2754"
                        r="1.42021"
                        transform="rotate(180 1.41963 50.2754)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="1.41963"
                        cy="26.1319"
                        r="1.42021"
                        transform="rotate(180 1.41963 26.1319)"
                        fill="#3056D3"
                      />
                      <circle
                        cx="1.41963"
                        cy="1.4202"
                        r="1.42021"
                        transform="rotate(180 1.41963 1.4202)"
                        fill="#3056D3"
                      />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
